import React, { useEffect, useRef, useState } from 'react'
import { Formik, Form } from 'formik'
import isEmail from 'validator/lib/isEmail'
import axios from 'axios'
import { FormField, SubmitButton } from '../ui/ui'
import { profile } from '../../data/portfolio'
import './SponsorDialog.scss'

const FORM_ENDPOINT = 'https://formspree.io/f/mrgdzgnr'
const PHONE_DIGITS = profile.phone.replace(/\D/g, '')

const channels = [
  { id: 'email', label: 'Email', icon: 'fas fa-envelope', field: 'Email address', type: 'email' },
  { id: 'whatsapp', label: 'WhatsApp', icon: 'fa-brands fa-whatsapp', field: 'WhatsApp number', type: 'tel' },
  { id: 'call', label: 'Phone call', icon: 'fas fa-phone', field: 'Phone number', type: 'tel' }
]

// Direct links for visitors who'd rather reach out right now.
export const directLinks = (topic) => {
  const text = `Hi Paradzai, I'd like to talk about: ${topic}.`
  return [
    { id: 'email', label: 'Email', icon: 'fas fa-envelope', href: `mailto:${profile.email}?subject=${encodeURIComponent(topic)}&body=${encodeURIComponent(`${text}\n\n`)}` },
    { id: 'whatsapp', label: 'WhatsApp', icon: 'fa-brands fa-whatsapp', href: `https://wa.me/${PHONE_DIGITS}?text=${encodeURIComponent(text)}`, external: true },
    { id: 'call', label: 'Call', icon: 'fas fa-phone', href: `tel:+${PHONE_DIGITS}` }
  ]
}

export const QuickContact = ({ topic, compact = false }) => (
  <div className={`sp-quick ${compact ? 'is-compact' : ''}`}>
    {directLinks(topic).map((link) => (
      <a
        key={link.id}
        className={`sp-quick__btn is-${link.id}`}
        href={link.href}
        target={link.external ? '_blank' : undefined}
        rel="noreferrer"
        aria-label={`${link.label} Paradzai about ${topic}`}
      >
        <i className={link.icon} /> <span>{link.label}</span>
      </a>
    ))}
  </div>
)

// Registration dialog: sponsor or mentee picks a channel, leaves details, and the request is emailed via Formspree.
const SponsorDialog = ({ open, onClose, kind = 'sponsor', tier }) => {
  const [status, setStatus] = useState('idle')
  const [done, setDone] = useState(null)
  const cardRef = useRef(null)
  const isMentee = kind === 'mentee'
  const topic = isMentee ? 'Mentorship application' : `Sponsorship: ${tier?.name}`

  useEffect(() => {
    if (!open) return undefined
    setStatus('idle')
    setDone(null)
    const onKey = (event) => { if (event.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    requestAnimationFrame(() => cardRef.current?.querySelector('input')?.focus())
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  if (!open) return null

  const validate = (values) => {
    const errors = {}
    if (!values.name.trim()) errors.name = 'Please add your name'
    if (!values.contact.trim()) errors.contact = values.channel === 'email' ? 'An email lets me reply' : 'A number lets me reach you'
    else if (values.channel === 'email' && !isEmail(values.contact)) errors.contact = "That email doesn't look right"
    else if (values.channel !== 'email' && !/^\+?[\d\s()-]{7,}$/.test(values.contact)) errors.contact = 'Include the country code, e.g. +44 7700 900123'
    if (isMentee && values.message.trim().length < 15) errors.message = 'Tell me a little about what you want to learn'
    return errors
  }

  const onSubmit = async (values) => {
    setStatus('loading')
    const channel = channels.find((item) => item.id === values.channel)
    try {
      const response = await axios.post(FORM_ENDPOINT, {
        subject: `${topic} (${channel.label})`,
        name: values.name,
        organisation: values.organisation,
        preferred_channel: channel.label,
        contact: values.contact,
        email: values.channel === 'email' ? values.contact : undefined,
        message: values.message || '(no message)'
      })
      if (response.status !== 200) throw new Error('Unexpected response')
      setStatus('success')
      setDone({ channel, contact: values.contact, name: values.name.trim().split(' ')[0] })
    } catch (error) {
      setStatus('error')
    }
  }

  return (
    <div className="sp-overlay" onMouseDown={onClose}>
      <div className="ui-page sp-card" ref={cardRef} role="dialog" aria-modal="true" aria-labelledby="sp-title" onMouseDown={(event) => event.stopPropagation()}>
        <button type="button" className="sp-close" onClick={onClose} aria-label="Close"><i className="fas fa-xmark" /></button>

        {done ? (
          <div className="sp-done">
            <svg className="sp-done__check" viewBox="0 0 52 52" aria-hidden="true"><circle cx="26" cy="26" r="24" /><path d="M15 27l7 7 15-16" /></svg>
            <h3 id="sp-title">You&apos;re registered, {done.name}!</h3>
            <p>I&apos;ll get back to you by <strong>{{ email: 'email', whatsapp: 'WhatsApp', call: 'phone call' }[done.channel.id]}</strong> at <strong>{done.contact}</strong>. Can&apos;t wait? Reach me directly:</p>
            <QuickContact topic={topic} />
          </div>
        ) : (
          <>
            <p className="sp-kicker"><i className={`fas ${isMentee ? 'fa-seedling' : 'fa-hand-holding-heart'}`} /> {isMentee ? 'Mentorship' : tier?.cadence}</p>
            <h3 id="sp-title">{isMentee ? 'Apply to be mentored' : `Get started as a ${tier?.name}`}</h3>
            <p className="sp-lead">
              {isMentee
                ? "Share where you are and what you want to build. I'll reach out on the channel you prefer."
                : "Leave your details and how you'd like me to reach you. I'll follow up personally to get started."}
            </p>

            <Formik initialValues={{ name: '', organisation: '', channel: 'whatsapp', contact: '', message: '' }} validate={validate} onSubmit={onSubmit}>
              {(formik) => {
                const channel = channels.find((item) => item.id === formik.values.channel)
                return (
                  <Form noValidate>
                    <p className="sp-label">How should I reach you?</p>
                    <div className="sp-channels" role="radiogroup" aria-label="Preferred contact channel">
                      {channels.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          role="radio"
                          aria-checked={formik.values.channel === item.id}
                          className={`sp-channel is-${item.id} ${formik.values.channel === item.id ? 'is-active' : ''}`}
                          onClick={() => { formik.setFieldValue('channel', item.id); formik.setFieldValue('contact', ''); formik.setFieldTouched('contact', false) }}
                        >
                          <i className={item.icon} /> {item.label}
                        </button>
                      ))}
                    </div>

                    <div className="ui-form-grid">
                      <FormField name="name" label="Your name" autoComplete="name" required />
                      <FormField name="organisation" label={isMentee ? 'School / current role' : 'Organisation'} hint="Optional" />
                      <FormField
                        key={channel.id}
                        className="span-2"
                        name="contact"
                        type={channel.type}
                        label={channel.field}
                        autoComplete={channel.type === 'email' ? 'email' : 'tel'}
                        hint={channel.type === 'tel' ? 'Include your country code' : undefined}
                        required
                      />
                      <FormField
                        className="span-2"
                        name="message"
                        as="textarea"
                        rows="3"
                        maxLength={800}
                        label={isMentee ? 'What do you want to learn or build?' : 'Anything I should know? (optional)'}
                        required={isMentee}
                      />
                    </div>

                    <div className="sp-actions">
                      <SubmitButton status={status} labels={{ idle: isMentee ? 'Send application' : 'Register interest', loading: 'Sending…', error: 'Try again' }} />
                      {status === 'error' && <span className="sp-error">Couldn&apos;t send. Please use a direct option below.</span>}
                    </div>
                  </Form>
                )
              }}
            </Formik>

            <div className="sp-direct">
              <span>or reach me directly</span>
              <QuickContact topic={topic} compact />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default SponsorDialog
